import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Play, Users, AlertTriangle, Network, RefreshCw, Eye } from "lucide-react";

interface ClusterMember {
  id: string;
  display_name: string;
  current_risk_score: number;
  network_role: string;
}

interface Cluster {
  id: string;
  cluster_name: string;
  member_ids: string[];
  cluster_risk_score: number;
  infected_member_count: number;
  is_infected: boolean;
  redundancy_score: number;
  detected_at: string;
  members?: ClusterMember[];
}

interface WideBridge {
  id: string;
  display_name: string;
  bridge_clusters: string[];
  current_risk_score: number;
}

interface ClusterAnalyticsProps {
  siteId?: string;
}

export const ClusterAnalytics = ({ siteId }: ClusterAnalyticsProps) => {
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [wideBridges, setWideBridges] = useState<WideBridge[]>([]);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState<Cluster | null>(null);
  const [clusterMembers, setClusterMembers] = useState<ClusterMember[]>([]);

  useEffect(() => {
    fetchClusters();
  }, [siteId]);

  const fetchClusters = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: profile } = await supabase
        .from("worker_profiles")
        .select("company_id")
        .eq("user_id", session.user.id)
        .single();

      if (!profile?.company_id) return;

      // Fetch workers first to get IDs for site filtering
      let workersQuery = supabase
        .from("worker_profiles")
        .select("id, display_name, current_risk_score, network_role")
        .eq("company_id", profile.company_id);

      if (siteId) {
        workersQuery = workersQuery.eq("site_id", siteId);
      }

      const { data: workers } = await workersQuery;
      const workerIds = workers?.map(w => w.id) || [];

      // Fetch clusters
      const { data: clusterData } = await supabase
        .from("worker_clusters")
        .select("*")
        .eq("company_id", profile.company_id)
        .order("cluster_risk_score", { ascending: false });

      // Enrich clusters with member details and filter by site
      let enrichedClusters = (clusterData || []).map(cluster => {
        const members = (cluster.member_ids || [])
          .map((id: string) => workers?.find(w => w.id === id))
          .filter(Boolean) as ClusterMember[];
        return { ...cluster, members };
      });

      // If site filter is active, only show clusters that have at least one member from the site
      if (siteId) {
        enrichedClusters = enrichedClusters.filter(cluster => 
          cluster.members && cluster.members.some(m => workerIds.includes(m.id))
        );
      }

      setClusters(enrichedClusters);

      // Fetch wide bridges - filtered by site
      let bridgeQuery = supabase
        .from("worker_profiles")
        .select("id, display_name, bridge_clusters, current_risk_score")
        .eq("company_id", profile.company_id)
        .eq("is_wide_bridge", true);

      if (siteId) {
        bridgeQuery = bridgeQuery.eq("site_id", siteId);
      }

      const { data: bridgeData } = await bridgeQuery;

      setWideBridges(bridgeData || []);
    } catch (error) {
      console.error("Error fetching clusters:", error);
    } finally {
      setLoading(false);
    }
  };

  const triggerClusterDetection = async () => {
    setDetecting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: profile } = await supabase
        .from("worker_profiles")
        .select("company_id")
        .eq("user_id", session.user.id)
        .single();

      if (!profile?.company_id) return;

      // First, clear existing clusters for this company
      const { error: deleteError } = await supabase
        .from("worker_clusters")
        .delete()
        .eq("company_id", profile.company_id);

      if (deleteError) {
        console.error("Error clearing old clusters:", deleteError);
      }

      // Then detect new clusters
      const { data, error } = await supabase.functions.invoke("detect-worker-clusters", {
        body: { companyId: profile.company_id, dateRange: 7 }
      });

      if (error) throw error;

      toast.success("Cluster detection complete", {
        description: `Found ${data.clustersDetected} clusters, ${data.wideBridges?.count || 0} wide bridges`
      });

      await fetchClusters();
    } catch (error) {
      console.error("Error detecting clusters:", error);
      toast.error("Failed to detect clusters");
    } finally {
      setDetecting(false);
    }
  };

  const openClusterDetail = (cluster: Cluster) => {
    setSelectedCluster(cluster);
    setClusterMembers(cluster.members || []);
  };

  const getRiskBadge = (score: number) => {
    if (score >= 70) return <Badge variant="destructive">High Risk</Badge>;
    if (score >= 40) return <Badge className="bg-warning text-warning-foreground">Medium</Badge>;
    return <Badge className="bg-success text-success-foreground">Low</Badge>;
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case "champion":
        return <Badge className="bg-success text-success-foreground">Champion</Badge>;
      case "bridge":
        return <Badge className="bg-primary text-primary-foreground">Bridge</Badge>;
      case "isolate":
        return <Badge variant="destructive">Isolate</Badge>;
      default:
        return <Badge className="bg-warning text-warning-foreground">Learner</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium">Detected Clusters</h4>
          <p className="text-sm text-muted-foreground">
            Groups of workers who frequently work together (Centola's Complex Contagion)
          </p>
        </div>
        <Button onClick={triggerClusterDetection} disabled={detecting}>
          {detecting ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Play className="h-4 w-4 mr-2" />
          )}
          Detect Clusters
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            Total Clusters
          </div>
          <div className="text-2xl font-bold mt-1">{clusters.length}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            Infected Clusters
          </div>
          <div className="text-2xl font-bold mt-1 text-destructive">
            {clusters.filter(c => c.is_infected).length}
          </div>
          <p className="text-xs text-muted-foreground mt-1">≥2 risky members</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Network className="h-4 w-4 text-primary" />
            Wide Bridges
          </div>
          <div className="text-2xl font-bold mt-1">{wideBridges.length}</div>
          <p className="text-xs text-muted-foreground mt-1">Cross-cluster connectors</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <RefreshCw className="h-4 w-4" />
            Avg Redundancy
          </div>
          <div className="text-2xl font-bold mt-1">
            {clusters.length > 0 
              ? (clusters.reduce((sum, c) => sum + (c.redundancy_score || 0), 0) / clusters.length).toFixed(2)
              : "0"}
          </div>
        </Card>
      </div>

      {/* Cluster List */}
      <div className="space-y-4">
        <h4 className="font-medium">Cluster Details</h4>
        {clusters.length === 0 ? (
          <Card className="p-6 text-center text-muted-foreground">
            No clusters detected. Click "Detect Clusters" to analyze worker interactions based on check-in data.
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {clusters.map((cluster, index) => (
              <Card key={cluster.id} className={`p-4 ${cluster.is_infected ? 'border-destructive/50 bg-destructive/5' : ''}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h5 className="font-medium">{cluster.cluster_name || `Cluster ${index + 1}`}</h5>
                    <p className="text-sm text-muted-foreground">
                      {cluster.member_ids?.length || 0} members
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {cluster.is_infected && (
                      <Badge variant="destructive" className="flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Infected
                      </Badge>
                    )}
                    {getRiskBadge(cluster.cluster_risk_score || 0)}
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                  <div>
                    <div className="text-muted-foreground">Risk Score</div>
                    <div className="font-medium">{cluster.cluster_risk_score || 0}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Risky Members</div>
                    <div className="font-medium text-destructive">{cluster.infected_member_count || 0}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Redundancy</div>
                    <div className="font-medium">{(cluster.redundancy_score || 0).toFixed(2)}</div>
                  </div>
                </div>

                {/* Member preview */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex -space-x-2">
                    {cluster.members?.slice(0, 4).map((member, i) => (
                      <div
                        key={member.id}
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium border-2 border-background ${
                          (member.current_risk_score || 0) > 60 ? 'bg-destructive text-destructive-foreground' : 'bg-muted'
                        }`}
                        title={member.display_name}
                      >
                        {member.display_name?.charAt(0).toUpperCase()}
                      </div>
                    ))}
                    {(cluster.members?.length || 0) > 4 && (
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium border-2 border-background">
                        +{(cluster.members?.length || 0) - 4}
                      </div>
                    )}
                  </div>
                </div>

                <Button variant="outline" size="sm" className="w-full" onClick={() => openClusterDetail(cluster)}>
                  <Eye className="h-4 w-4 mr-2" />
                  View Members
                </Button>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Wide Bridges */}
      <div className="space-y-4">
        <h4 className="font-medium">Wide Bridges</h4>
        <p className="text-sm text-muted-foreground">
          Workers connecting multiple clusters - key for behavior spread across groups
        </p>
        
        {wideBridges.length === 0 ? (
          <Card className="p-4 text-center text-muted-foreground text-sm">
            No wide bridges identified. These are workers who connect different clusters.
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {wideBridges.map(bridge => (
              <Card key={bridge.id} className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{bridge.display_name}</div>
                    <div className="text-xs text-muted-foreground">
                      Connects {bridge.bridge_clusters?.length || 0} clusters
                    </div>
                  </div>
                  {getRiskBadge(bridge.current_risk_score || 0)}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Cluster Member Dialog */}
      <Dialog open={!!selectedCluster} onOpenChange={() => setSelectedCluster(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedCluster?.cluster_name}
              {selectedCluster?.is_infected && (
                <Badge variant="destructive">Infected</Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="text-center p-2 bg-muted rounded">
                <div className="text-muted-foreground">Risk Score</div>
                <div className="text-xl font-bold">{selectedCluster?.cluster_risk_score || 0}</div>
              </div>
              <div className="text-center p-2 bg-muted rounded">
                <div className="text-muted-foreground">Members</div>
                <div className="text-xl font-bold">{clusterMembers.length}</div>
              </div>
              <div className="text-center p-2 bg-muted rounded">
                <div className="text-muted-foreground">Redundancy</div>
                <div className="text-xl font-bold">{(selectedCluster?.redundancy_score || 0).toFixed(2)}</div>
              </div>
            </div>

            <div>
              <div className="text-sm font-medium mb-2">Cluster Members</div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {clusterMembers.map(member => (
                  <div key={member.id} className="flex items-center justify-between p-2 bg-muted rounded">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                        (member.current_risk_score || 0) > 60 ? 'bg-destructive text-destructive-foreground' : 'bg-background'
                      }`}>
                        {member.display_name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-sm">{member.display_name}</div>
                        <div className="text-xs text-muted-foreground">Risk: {member.current_risk_score || 0}</div>
                      </div>
                    </div>
                    {getRoleBadge(member.network_role || "learner")}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
